import { Injectable } from '@angular/core';
import { LocalDate } from 'src/core/common/domain/date/local-date';
import { AppService } from 'src/app/app.service';
import { SessionService } from 'src/core/session/domain/session.service';
import { LoadMasterData } from 'src/core/master-data/application/load/load-master-data';
import { FiltersService } from 'src/core/filters/domain/filters-service';
import { LoadingHelper } from 'src/app/helpers/loading-helper';
import { TranslatorService } from 'src/core/translator/domain/translator-service';
import { MessageHelper } from 'src/app/helpers/message-helper';
import { MasterDataStructureInvalidException } from 'src/core/common/domain/exceptions/master-data-structure-invalid-exception';
import { ConnectionException } from 'src/core/common/domain/exceptions/connection-exception';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private static readonly THRESHOLD_IN_MINUTES = 12 * 60; // 12 hours
  // private static readonly THRESHOLD_IN_MINUTES = 1; // 1 minute, for testing

  constructor(
    private readonly appService: AppService,
    private readonly filtersService: FiltersService,
    private readonly loadingHelper: LoadingHelper,
    private readonly loadMasterData: LoadMasterData,
    private readonly translation: TranslatorService,
    private readonly messageHelper: MessageHelper,
    private readonly session: SessionService
  ) {}

  async checkCurrentData() {
    const isAuthenticated = await this.session.isAuthenticatedPromise();
    if (!isAuthenticated) {
      return;
    }

    // const isDataOutdated = true; // Uncomment to force data is outdated
    const isDataOutdated = await this.isDataOutdated();
    if (!isDataOutdated) {
      console.log('Current APP data (master data, filters) is still valid');
      return;
    }

    await this.downloadMasterData(); // throws NoConnection
    await this.updateFilters();
  }

  private async downloadMasterData() {
    await this.loadingHelper.start(this.translation.translate('MESSAGES.DOWNLOADING_MASTER_DATA'));
    try {
      await this.loadMasterData.execute();
    } catch (exception) {
      if (exception instanceof ConnectionException) {
        // don't show any toast since here it is not needed
      }
      if (exception instanceof MasterDataStructureInvalidException) {
        await this.messageHelper.showError(this.translation.translate('ERRORS.MASTER_DATA_INVALID_STRUCTURE'));
      }
    }
    await this.loadingHelper.end();
  }

  private async updateFilters() {
    await this.filtersService.updateFilters();
  }

  private async isDataOutdated(): Promise<boolean> {
    // Data must be reset if last put-to-background time was yesterday, more than 12 hours ago, and user is still logged in.
    const isAuthenticated = await this.session.isAuthenticatedPromise();
    if (!isAuthenticated) {
      return false;
    }

    const startPauseTime = await this.appService.getAppPutToBackgroundTime();
    const now = LocalDate.now();
    // "Yesterday" is a day on the tenant's calendar, so both sides are read in the tenant timezone.
    const startedAt = startPauseTime ?? now;
    const diffInMinutes = now.date.diff(startedAt.date, 'minutes');
    const sameDay = startedAt.date.isSame(now.date, 'day');
    const currentDataIsTooOld = diffInMinutes > DataService.THRESHOLD_IN_MINUTES;
    const dataIsStillValid = !currentDataIsTooOld && sameDay;
    console.warn(
      `Current stored data (master data, filters) is ${diffInMinutes} seconds old. It is ${
        dataIsStillValid ? 'still valid' : 'outdated'
      }`
    );
    return !dataIsStillValid;
  }
}
